
interface LoadingScreenProps {
  message?: string
  size?: 'small' | 'medium' | 'large'
  overlay?: boolean
}

export function LoadingScreen({ 
  message = 'Loading...', 
  size = 'medium',
  overlay = false 
}: LoadingScreenProps) {
  const sizeClasses = {
    small: 'w-6 h-6',
    medium: 'w-12 h-12',
    large: 'w-16 h-16'
  }

  const content = (
    <div className="flex flex-col items-center justify-center space-y-4">
      {/* Animated logo/spinner */}
      <div className="relative">
        <div className={`${sizeClasses[size]} animate-spin`}>
          <svg 
            className="w-full h-full text-accent-500" 
            fill="none" 
            viewBox="0 0 24 24"
          >
            <circle 
              className="opacity-25" 
              cx="12" 
              cy="12" 
              r="10" 
              stroke="currentColor" 
              strokeWidth="4"
            />
            <path 
              className="opacity-75" 
              fill="currentColor" 
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
        
        {/* Pulse effect */}
        <div className={`absolute inset-0 ${sizeClasses[size]} rounded-full bg-accent-500 opacity-20 animate-ping`} />
      </div>

      {/* Loading message */}
      {message && (
        <div className="text-center">
          <p className="text-white font-medium mb-1">{message}</p>
          <div className="flex space-x-1 justify-center">
            <div className="w-2 h-2 bg-accent-500 rounded-full animate-bounce" />
            <div className="w-2 h-2 bg-accent-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
            <div className="w-2 h-2 bg-accent-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
          </div>
        </div>
      )}
    </div>
  )

  if (overlay) {
    return (
      <div className="fixed inset-0 bg-dark-950/80 backdrop-blur-sm z-50 flex items-center justify-center">
        {content}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-dark-950 flex items-center justify-center">
      {content}
    </div>
  )
}

interface LoadingSpinnerProps {
  size?: 'small' | 'medium' | 'large'
  className?: string
}

export function LoadingSpinner({ size = 'medium', className = '' }: LoadingSpinnerProps) {
  const sizeClasses = {
    small: 'w-4 h-4',
    medium: 'w-6 h-6',
    large: 'w-8 h-8'
  }

  return (
    <div className={`${sizeClasses[size]} animate-spin ${className}`}>
      <svg 
        className="w-full h-full text-current" 
        fill="none" 
        viewBox="0 0 24 24"
      >
        <circle 
          className="opacity-25" 
          cx="12" 
          cy="12" 
          r="10" 
          stroke="currentColor" 
          strokeWidth="4"
        />
        <path 
          className="opacity-75" 
          fill="currentColor" 
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  )
}

interface LoadingSkeletonProps {
  className?: string
  lines?: number
  avatar?: boolean
}

export function LoadingSkeleton({ className = '', lines = 3, avatar = false }: LoadingSkeletonProps) {
  return (
    <div className={`animate-pulse ${className}`}>
      <div className="flex space-x-4">
        {avatar && (
          <div className="rounded-full bg-dark-700 h-10 w-10 flex-shrink-0" />
        )}
        <div className="flex-1 space-y-2">
          {Array.from({ length: lines }).map((_, i) => (
            <div 
              key={i}
              className={`bg-dark-700 rounded h-4 ${
                i === lines - 1 ? 'w-3/4' : 'w-full'
              }`} 
            />
          ))}
        </div>
      </div>
    </div>
  )
}

interface LoadingCardProps {
  count?: number
  className?: string
}

export function LoadingCard({ count = 1, className = '' }: LoadingCardProps) {
  return (
    <div className={`space-y-4 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="card p-6 animate-pulse">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-12 h-12 bg-dark-700 rounded-full" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-dark-700 rounded w-1/4" />
              <div className="h-3 bg-dark-700 rounded w-1/3" />
            </div>
          </div>
          <div className="space-y-3">
            <div className="h-4 bg-dark-700 rounded" />
            <div className="h-4 bg-dark-700 rounded w-5/6" />
            <div className="h-4 bg-dark-700 rounded w-3/4" />
          </div>
          <div className="mt-4 flex space-x-4">
            <div className="h-8 bg-dark-700 rounded w-20" />
            <div className="h-8 bg-dark-700 rounded w-20" />
          </div>
        </div>
      ))}
    </div>
  )
}